import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'selected', label: '', sortable: false, class: 'text-center' },
    { key: 'fullname', label: 'Full Name', sortable: false },
    { key: 'workTitle', label: 'Work Title', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false },
  ]
  const perPage = ref(10)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const debouncedSearchQuery = ref('')
  let searchDebounceTimer = null
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)
  const items = ref([])
  const isLoading = ref(false)
  const selectedIds = ref([])

  const groupId = computed(() => router.currentRoute.query.gid)

  const dataMeta = computed(() => {
    const localItemsCount = items.value ? items.value.length : 0
    const from = totalUsers.value === 0 ? 0 : perPage.value * (currentPage.value - 1) + 1
    return {
      from,
      to: totalUsers.value === 0 ? 0 : Math.min(from + localItemsCount - 1, totalUsers.value),
      of: totalUsers.value,
    }
  })

  const pageIds = computed(() => (items.value || []).map(item => item.id))

  const isAllSelectedOnPage = computed(() => {
    if (!pageIds.value.length) return false
    return pageIds.value.every(id => selectedIds.value.includes(id))
  })

  const isRowSelected = id => selectedIds.value.includes(id)

  const clearSelection = () => {
    selectedIds.value = []
  }

  const toggleRow = id => {
    if (selectedIds.value.includes(id)) {
      selectedIds.value = selectedIds.value.filter(selectedId => selectedId !== id)
    } else {
      selectedIds.value = selectedIds.value.concat(id)
    }
  }

  const toggleSelectAll = () => {
    if (isAllSelectedOnPage.value) {
      selectedIds.value = selectedIds.value.filter(id => !pageIds.value.includes(id))
      return
    }
    const merged = selectedIds.value.slice()
    pageIds.value.forEach(id => {
      if (!merged.includes(id)) merged.push(id)
    })
    selectedIds.value = merged
  }

  const refetchData = () => {
    fetchGroupMembers()
  }

  watch(searchQuery, (newValue) => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      debouncedSearchQuery.value = newValue
    }, 300)
  })

  const fetchGroupMembers = () => {
    if (!groupId.value) {
      items.value = []
      totalUsers.value = 0
      clearSelection()
      return
    }

    isLoading.value = true
    store
      .dispatch('client-groups/fetchGroupMembers', {
        id: groupId.value,
        page: currentPage.value,
        limit: perPage.value,
        search: debouncedSearchQuery.value,
      })
      .then(response => {
        const { users, total } = response.data
        items.value = users || []
        totalUsers.value = typeof total === 'number' ? total : (users ? users.length : 0)

        const totalPages = Math.max(1, Math.ceil(totalUsers.value / perPage.value) || 1)
        if (totalUsers.value > 0 && currentPage.value > totalPages) {
          currentPage.value = totalPages
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  watch(groupId, () => {
    clearSelection()
    currentPage.value = 1
    fetchGroupMembers()
  }, { immediate: true })

  watch(debouncedSearchQuery, () => {
    clearSelection()
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }
    fetchGroupMembers()
  })

  watch([currentPage, perPage], () => {
    clearSelection()
    fetchGroupMembers()
  })

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === null) return 'primary'
    return 'success'
  }
  const resolveUserStatusVariantText = status => {
    if (status === null) return 'primary'
    return status
  }

  const resolveUserRolesArray = roles => {
    var rolesArray = []
    if (roles && roles.length > 0) {
      for (var i = 0; i < roles.length; i++) {
        rolesArray.push(roles[i].description)
      }
    }
    return rolesArray
  }

  return {
    fetchGroupMembers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    items,
    isLoading,
    selectedIds,
    isAllSelectedOnPage,
    isRowSelected,
    toggleRow,
    toggleSelectAll,
    clearSelection,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    resolveUserStatusVariantText,
    refetchData,
    resolveUserRolesArray,
    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
